import styles from "./RoomAmenities.module.css";

// Font Awesome 6 (free, solid) icon per amenity, picked by keywords in the
// amenity name. First match wins, so the more specific patterns come first
// (e.g. "towels (bath ...)" before "bath", "smoke detector" before "smoking").
// Covers room amenities (booking page) and hotel features (hotel page).
const AMENITY_ICONS = [
  [/wi-?fi|internet/i, "fa-wifi"],
  [/air ?condition|\ba\/?c\b|cooling/i, "fa-snowflake"],
  [/heating|heater/i, "fa-temperature-half"],
  [/smoke (detector|alarm)/i, "fa-smog"],
  [/fire extinguisher|fire safety/i, "fa-fire-extinguisher"],
  [/non-?smoking|no smoking/i, "fa-ban-smoking"],
  [/smoking/i, "fa-smoking"],
  [/first aid|medical|doctor/i, "fa-kit-medical"],
  [/front desk|reception|concierge/i, "fa-bell-concierge"],
  [/house ?keeping|cleaning/i, "fa-broom"],
  [/cctv|camera|surveillance/i, "fa-video"],
  [/towel/i, "fa-soap"],
  [/toiletr|shampoo|soap/i, "fa-pump-soap"],
  [/bath ?tub|jacuzzi|hot tub/i, "fa-bath"],
  [/shower|hot water|geyser/i, "fa-shower"],
  [/bathroom|toilet|washroom/i, "fa-toilet"],
  [/hair ?dryer/i, "fa-wind"],
  [/bedding|\bbeds?\b|mattress|pillow|blanket|linen/i, "fa-bed"],
  [/room service/i, "fa-bell-concierge"],
  [/door lock|\block\b|key ?card/i, "fa-lock"],
  [/locker|\bsafe\b|safety deposit/i, "fa-vault"],
  [/connecting room|interconnect/i, "fa-door-open"],
  [/blackout|curtain/i, "fa-moon"],
  [/bottled water|drinking water|water bottle/i, "fa-bottle-water"],
  [/television|\btv\b|satellite|cable/i, "fa-tv"],
  [/closet|wardrobe|hanger|\biron/i, "fa-shirt"],
  [/laundry|washing machine|dry clean/i, "fa-jug-detergent"],
  [/luggage|baggage/i, "fa-suitcase-rolling"],
  [/breakfast/i, "fa-mug-saucer"],
  [/coffee|\btea\b|kettle/i, "fa-mug-hot"],
  [/fridge|refrigerator/i, "fa-temperature-low"],
  [/mini ?bar|\bbar\b|lounge/i, "fa-martini-glass"],
  [/kitchen|microwave|stove|cook/i, "fa-kitchen-set"],
  [/bbq|barbecue|bonfire|campfire/i, "fa-fire"],
  [/meal|dining|restaurant|food|cafe|café/i, "fa-utensils"],
  [/airport|shuttle|pick-?up|drop/i, "fa-van-shuttle"],
  [/car rental|taxi|cab\b/i, "fa-car"],
  [/bicycle|cycle|bike/i, "fa-bicycle"],
  [/tour|travel desk|sightseeing/i, "fa-map-location-dot"],
  [/currency|exchange/i, "fa-money-bill-transfer"],
  [/\batm\b|cash/i, "fa-money-bill"],
  [/conference|meeting|banquet|event/i, "fa-people-group"],
  [/business|desk|work ?space|work area/i, "fa-briefcase"],
  [/balcony|\bviews?\b|terrace|sea facing/i, "fa-mountain-sun"],
  [/parking/i, "fa-square-parking"],
  [/pool|swimming/i, "fa-person-swimming"],
  [/gym|fitness/i, "fa-dumbbell"],
  [/\bspa\b|massage|sauna|steam/i, "fa-spa"],
  [/kids|children|play ?area|play ?ground/i, "fa-child"],
  [/game|indoor games|board games/i, "fa-gamepad"],
  [/library|books?\b/i, "fa-book"],
  [/telephone|phone/i, "fa-phone"],
  [/elevator|\blift\b/i, "fa-elevator"],
  [/wheelchair|accessib/i, "fa-wheelchair"],
  [/\bfans?\b|ceiling fan/i, "fa-fan"],
  [/security|guard/i, "fa-shield-halved"],
  [/lamp|\blight/i, "fa-lightbulb"],
  [/charging|power|socket|outlet|plug/i, "fa-plug"],
  [/sofa|couch|seating/i, "fa-couch"],
  [/\bpets?\b|pet[- ]friendly/i, "fa-paw"],
  [/\bcrib\b|\bcot\b|baby/i, "fa-baby-carriage"],
  [/beach/i, "fa-umbrella-beach"],
  [/garden|lawn/i, "fa-tree"],
  // generic "24-hour ..." last, so "24-Hour Security" etc. keep their own icon
  [/wake-?up|24-?hour|24\/7|round the clock/i, "fa-clock"],
];

export const getAmenityIcon = (name = "") => {
  const match = AMENITY_ICONS.find(([pattern]) => pattern.test(name));
  return match ? match[1] : "fa-circle-check";
};

// "Comfortable Bedding (high-quality linens, pillows)" ->
// { title: "Comfortable Bedding", detail: "high-quality linens, pillows" }
// Only a bracket after a space is a detail: "Restaurant(s)" stays whole.
const splitAmenity = (name = "") => {
  const text = String(name).trim();
  const match = text.match(/^(.*?\S)\s+\((.+)\)\s*$/);
  return match ? { title: match[1], detail: match[2] } : { title: text };
};

// Amenities as an icon grid (no bullets). Used on the room cards, the room
// photo preview modal and the hotel page. `color` tints the icons with the
// hotel theme; size="lg" is the bigger hotel-page variant.
export function AmenityList({
  amenities = [],
  color,
  className = "",
  size,
}) {
  if (!amenities?.length) return null;
  return (
    <ul
      className={`${styles.amenityList} ${
        size === "lg" ? styles.amenityListLg : ""
      } ${className}`}
      style={color ? { "--amenity-color": color } : undefined}
    >
      {amenities.map((amenity, i) => {
        const { title, detail } = splitAmenity(amenity);
        return (
          <li className={styles.amenityItem} key={`${amenity}-${i}`}>
            <span className={styles.amenityIcon} aria-hidden="true">
              <i className={`fa-solid ${getAmenityIcon(amenity)}`}></i>
            </span>
            <span className={styles.amenityText}>
              <span className={styles.amenityTitle}>{title}</span>
              {detail && (
                <span className={styles.amenityDetail}>{detail}</span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// Room description ("About this room"); keeps the line breaks typed in admin.
export function RoomAbout({ text, color }) {
  if (!text || !String(text).trim()) return null;
  return (
    <div
      className={styles.roomAbout}
      style={color ? { "--amenity-color": color } : undefined}
    >
      <span className={styles.roomAboutTitle}>About this room</span>
      <span className={styles.roomAboutText}>{String(text).trim()}</span>
    </div>
  );
}

// "See more" / "See less" toggle with a chevron.
export function SeeMoreToggle({
  expanded,
  onClick,
  extraCount = 0,
  className = "",
  moreLabel,
}) {
  return (
    <button
      type="button"
      className={`${styles.seeMoreBtn} ${className}`}
      onClick={onClick}
      aria-expanded={expanded}
    >
      {expanded
        ? "See less"
        : moreLabel ||
          (extraCount > 0
            ? `See more (+${extraCount} amenities)`
            : "See more")}
      <i
        className={`fa-solid ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`}
        aria-hidden="true"
      ></i>
    </button>
  );
}
